import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  login,
  register,
  UserSession
} from '../utils'
import { Client, Account, OAuthProvider } from 'appwrite'
import logo from "../assets/tom-logo-icon-black.jpg"
import { AppProvider } from '@toolpad/core/AppProvider';
import { SignInPage } from '@toolpad/core/SignInPage';
import { useTheme, createTheme } from '@mui/material/styles';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Link,
  IconButton,
  Button,
  Stack,
  Typography,
  TextField,
  Divider,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';


const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID)

const account = new Account(client)


const providers = [/*{ id: 'google', name: 'Google' },*/ { id: 'credentials', name: 'Credentials' }];

const authLogin = async(provider) => {
  account.createOAuth2Session(
    OAuthProvider[provider],
    'http://localhost:5173',
    'http://localhost:5173'
  );
}

function LogoButton({ redirect }) {
  const navigate = useNavigate();

  return (
    <Button
      variant="text"
      onClick={() => navigate(redirect || '/')}
      sx={{
        position: 'relative',
        display: 'flex',
        mt: '-5%',
        mb: '5%',
        height: '2.5em',
        width: 'fit-content',
        alignItems: 'center'
      }}
    >
      <ArrowBackIosNewOutlinedIcon sx={{ mr: '0.5em', fontSize: '1em' }} />
      Back&nbsp;
    </Button>
  );
}

const GoogleLogo = () => {
  return (<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    <path d="M1 1h22v22H1z" fill="none"></path>
  </svg>)
}


export default function Login() {
  const navigate = useNavigate()
  const [session, setSession] = useAtom(UserSession)

  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("info");
  const [creating, setCreating] = useState(false);

  var googleSignIn = false;

  const urlParams = new URLSearchParams(window.location.search);
  const redirect = urlParams.get('redirect');
  const signout = urlParams.get('signout');

  const email = useRef();
  const password = useRef();
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();


  const BRANDING = {
    logo: (<>
      <LogoButton redirect={redirect}/>
      <Stack direction="row" justifyContent="center" alignItems="center" gap={1.5}>
        <img
          src={logo}
          alt="Takeoff Monkey Widgets Logo"
          style={{ height: 48 }}
        />
      </Stack>
    </>),
    title: 'Takeoff Monkey Widgets',
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const user = await account.get()
        if (user) navigate('/');
      } catch (error) {}
    }

    if (signout) account.deleteSession('current');
    else checkSession();
  }, [])
  
  const handleLogin = async (provider, formData) => {
    const emailRef = email.current?.value || '';
    const passwordRef = password.current?.value || '';
    setCreating(true);

    if (provider && provider.name !== 'Credentials') {
      authLogin(provider.name)
    } else {
      try {
        const user = await account.createEmailPasswordSession(emailRef, passwordRef).then(() => {
          return account.get()
        })
        setSession(user);

        if (redirect) {
          navigate(redirect);
        }
        else navigate('/');
      } catch (error) {
        console.error(error);
        if (error.message.includes('Invalid `email` param')) {
          setAlertType("warning");
          setOpen("Please enter your account email.");
        } else if (error.message.includes('Invalid `password` param')) {
          setAlertType("warning");
          setOpen("Please enter your account password.");
        } else if (error.message.includes('Invalid credentials')) {
          setAlertType("error");
          setOpen("Failed to sign in. Please verify your email and password are correct.");
        } else {
          setAlertType("error");
          setOpen("Sign-in error: " + error.message);
        }
        setCreating(false);
      }
    }
  }


  return (<>
    <AppProvider branding={BRANDING}>
      <Stack direction="row" sx={{ maxWidth: '500px', mx: 'auto' }}>
        <SignInPage
          signIn={handleLogin}
          providers={providers}
          slots={{
            title: () => <Typography variant="h4" sx={{ mt: 1, mb: 2, textAlign: 'center' }}>Sign in</Typography>,
            subtitle: () => googleSignIn && <><Button variant="contained" fullWidth size="large" sx={{ my: 2 }}>
              <GoogleLogo />
              &nbsp;&nbsp;Sign in with Google
            </Button><Divider sx={{ width: '100%', fontSize: '1.1em' }}>or</Divider></>,
            emailField: () => <TextField
              autoComplete
              inputRef={email}
              defaultValue={email.current?.value}
              fullWidth
              label="Email"
              variant="outlined"
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { height: '2.5em', overflow: 'hidden' } }}
            />,
            passwordField: () => <FormControl
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '2.5em',
                  fontSize: '1em',
                  overflow: 'hidden'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1em',
                  top: '-6px',
                },
                '& .MuiInputLabel-shrink': {
                  top: 0,
                },
              }}
            >
              <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                inputRef={password}
                defaultValue={password.current?.value}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'hide password' : 'show password'}
                      onClick={toggleShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                      sx={{ mr: '7px' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                size="small"
              />
            </FormControl>,
            forgotPasswordLink: () => <Link
              variant="body2"
              onClick={() => navigate('/reset-password')}
              sx={{
                position: 'relative',
                display: 'flex',
                height: '2em',
                width: 'fit-content',
                alignItems: 'center',
                cursor: 'pointer'
              }}
            >
              Forgot password?
            </Link>,
            submitButton: () => <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2 }}
            >
              {creating ? <CircularProgress size={25} color='inherit' /> : "Sign In"}
            </Button>,
            rememberMe: () => null
          }}
          sx={{ mx: 'auto' }}
        />
      </Stack>
    </AppProvider>

    <Snackbar
      open={Boolean(open)}
      onClose={() => setOpen(false)}
      autoHideDuration={10000}
      key="snackbar"
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity={alertType}>
        {/* <AlertTitle>Error</AlertTitle> */}
        {open}
      </Alert>
    </Snackbar>
  </>);
}